import { REPORTING_TYPE_ENUM } from 'app/types/enums/reportingTypes';
import { useAPI } from '@providers/useAPI';

import { normalizeKeyTerms } from '@helpers/keyTerms/normalizeKeyTerms';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateJobBodyDto,
  type GetManyForUserResponseDto,
  type JobDtoUser,
  JobResponseDto,
  JobsService,
} from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { createFetchJobQueryFn } from 'app/queries/jobs/createFetchJobQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { createFetchAvailableLayoutsQueryFn } from 'app/queries/layouts/createFetchAvailableLayoutsQueryFn';
import { layoutsKeys } from 'app/queries/layouts/layoutsKeys';
import { createFetchJobMediaFilesQueryFn } from 'app/queries/media/createFetchJobMediaFilesQueryFn';
import { mediaKeys } from 'app/queries/media/mediaKeys';
import { TUser } from 'app/types/entities/TUser';
import { ensureDraftJob } from '@pages/User/NewJobPage/helpers/ensureDraftJobBeforeUpload';
import { hasExistingJobLoadFailed } from '@pages/User/NewJobPage/helpers/hasExistingJobLoadFailed';
import {
  createContext,
  type Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import { v4 } from 'uuid';

export type NewJobFormItem = CreateJobBodyDto & {
  userRequestedForeignLanguageRemoval?: boolean;
  verbatim?: boolean;
  repository?: { id: string };
};

type TForm = {
  item: NewJobFormItem;
  newAttorneys: string[];
  meetingAssignee?: JobDtoUser;
};

type TFormItemValue = string | boolean | number | null | TUser | Date | string[] | undefined;

type ServerMediaItem = { Id?: string; Name?: string; Size?: string | number; duration?: number };

const NO_SERVER_MEDIA_ITEMS: ServerMediaItem[] = [];
const NO_LAYOUTS: GetManyForUserResponseDto[] = [];

type TState = {
  isNew: boolean;
  jobId: string;
  isInitialLoading: boolean;
  isLoadError: boolean;
  isLoading: boolean;
  isSaving: boolean;
  setIsSaving: (v: boolean) => void;
  job: JobResponseDto | null;
  setJob: Dispatch<SetStateAction<JobResponseDto | null>>;
  form: TForm;
  setForm: Dispatch<SetStateAction<TForm>>;
  setFormItemValue: (n: string, v: TFormItemValue) => void;
  serverMediaItems: ServerMediaItem[];
  layouts: GetManyForUserResponseDto[] | undefined;
  ensureDraftJob: () => Promise<string | null>;
  ensureDraftJobBeforeUpload: () => Promise<string | null>;
};

const PageContext = createContext<TState>({
  isNew: true,
  jobId: '',
  isInitialLoading: false,
  isLoadError: false,
  isLoading: false,
  isSaving: false,
  setIsSaving: () => {},
  job: null,
  setJob: () => {},
  form: {
    item: {
      id: '',
      name: '',
      multichannel: false,
      userRequestedForeignLanguageRemoval: true,
      multipleFilesHandling: 'concat',
      meetingTime: '12:00',
      duration: 0,
      reportingType: REPORTING_TYPE_ENUM.REPORTING_TYPE_FTR,
      status: 'JOB_STATUS_DRAFT',
      notes: '',
      keyTerms: [],
      layoutId: undefined,
    } as NewJobFormItem,
    newAttorneys: [],
  },
  setForm: () => {},
  setFormItemValue: () => {},
  serverMediaItems: [],
  layouts: [],
  ensureDraftJob: async () => null,
  ensureDraftJobBeforeUpload: async () => null,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const navigate = useNavigate();
  const params = useParams() as { id?: string };
  const { workspace, organization } = useAuthContext();
  const workspaceId = workspace?.id ?? '';
  const organizationId = organization?.id ?? '';
  const hasContext = workspaceId !== '' && organizationId !== '';

  const [isSaving, setIsSaving] = useState(false);

  const isNew = !params?.id;
  const initialJobId = useMemo(() => (params.id as string) ?? v4(), [params.id]);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [job, setJob] = useState<JobResponseDto | null>(null);
  const [form, setForm] = useState<TForm>({
    item: {
      id: initialJobId,
      name: '',
      multichannel: false,
      userRequestedForeignLanguageRemoval: true,
      multipleFilesHandling: 'concat',
      meetingTime: '12:00',
      meetingTimezone: tz,
      duration: 0,
      reportingType: REPORTING_TYPE_ENUM.REPORTING_TYPE_FTR,
      status: 'JOB_STATUS_DRAFT',
      notes: '',
      keyTerms: [],
      layoutId: undefined,
    },
    newAttorneys: [],
  });
  const jobId = form.item.id || initialJobId;
  const formItemRef = useRef(form.item);
  const jobCreationPromiseRef = useRef<Promise<string | null> | null>(null);
  const seededJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    formItemRef.current = form.item;
  }, [form.item]);

  const { mutateAsync: createDraftJob } = useMutation({
    mutationFn: (request: Parameters<typeof JobsService.createJob>[0]) => call(() => JobsService.createJob(request)),
  });

  const ensureDraftJobFn = useCallback(async (): Promise<string | null> => {
    return ensureDraftJob({
      createJob: createDraftJob,
      workspaceId: workspace?.id,
      formItem: formItemRef.current,
      persistedJobId: job?.id,
      setJob,
      setFormItemId: (id) => {
        setForm((prev) => ({
          ...prev,
          item: {
            ...prev.item,
            id,
          },
        }));
      },
      creationPromiseRef: jobCreationPromiseRef,
    });
  }, [createDraftJob, workspace?.id, job?.id]);

  const shouldLoadJob = hasContext && (!isNew || Boolean(job?.id));

  const layoutsQuery = useQuery({
    queryKey: layoutsKeys.availableByOrganization(organizationId),
    queryFn: createFetchAvailableLayoutsQueryFn(call),
    enabled: hasContext,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const jobQuery = useQuery({
    queryKey: jobsKeys.workspaceDetail(jobId, workspaceId),
    queryFn: createFetchJobQueryFn(call),
    enabled: shouldLoadJob,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const mediaFilesQuery = useQuery({
    queryKey: mediaKeys.filesList(jobId),
    queryFn: createFetchJobMediaFilesQueryFn(call),
    enabled: shouldLoadJob,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useNotifyQueryError(layoutsQuery.error);
  useNotifyQueryError(jobQuery.error);
  useNotifyQueryError(mediaFilesQuery.error);

  const isJobAwaitingFirstFetch =
    shouldLoadJob && (!jobQuery.isFetchedAfterMount || !mediaFilesQuery.isFetchedAfterMount);
  const isInitialLoading = !hasContext || layoutsQuery.isLoading || isJobAwaitingFirstFetch;
  const isLoading = isInitialLoading || isSaving;
  const isLoadError = hasExistingJobLoadFailed({
    isNew,
    hasJob: Boolean(job),
    isJobError: jobQuery.isError,
    hasMediaFiles: Boolean(mediaFilesQuery.data),
    isMediaFilesError: mediaFilesQuery.isError,
  });

  const serverMediaItems = useMemo(
    () =>
      mediaFilesQuery.isFetchedAfterMount && mediaFilesQuery.data
        ? ((mediaFilesQuery.data.media ?? []) as ServerMediaItem[])
        : NO_SERVER_MEDIA_ITEMS,
    [mediaFilesQuery.isFetchedAfterMount, mediaFilesQuery.data],
  );

  const fetchedJob = jobQuery.isFetchedAfterMount ? jobQuery.data : undefined;

  useEffect(() => {
    if (!fetchedJob || seededJobIdRef.current === fetchedJob.id) {
      return;
    }
    seededJobIdRef.current = fetchedJob.id;

    if (fetchedJob.asOneLocked) {
      navigate(routes.jobsList.make(organization?.slug ?? '', workspace?.slug ?? ''));
      return;
    }
    setJob(fetchedJob);
    setForm((prev) => ({
      ...prev,
      item: {
        ...(fetchedJob as CreateJobBodyDto),
        name: fetchedJob.name !== 'Unnamed' ? fetchedJob.name ?? prev.item.name : prev.item.name,
        meetingTime: fetchedJob.meetingTime ?? '12:00',
        meetingTimezone: fetchedJob.meetingTimezone ?? tz,
        deadline: prev.item.deadline ?? fetchedJob.deadline ?? undefined,
        keyTerms: normalizeKeyTerms(fetchedJob.keyTerms ?? []),
      },
    }));
  }, [fetchedJob, navigate, organization?.slug, workspace?.slug, tz]);

  const setFormItemValue = (name: string, value: TFormItemValue) => {
    setForm((prev) => ({
      ...prev,
      item: {
        ...prev.item,
        [name]: value,
      },
    }));
  };

  return (
    <PageContext.Provider
      value={{
        isNew,
        jobId,
        isInitialLoading,
        isLoadError,
        isLoading,
        isSaving,
        setIsSaving,
        job,
        setJob,
        form,
        setForm,
        setFormItemValue,
        serverMediaItems,
        layouts: layoutsQuery.data ?? NO_LAYOUTS,
        ensureDraftJob: ensureDraftJobFn,
        ensureDraftJobBeforeUpload: ensureDraftJobFn,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
